import React, { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import {
    getRentalCategories,
    createRentalCategory,
    updateRentalCategory,
    deleteRentalCategory,
} from "../../../services/rentalService";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import ImageInput from "../../common/ImageInput";
import Pagination from "../../common/Pagination";
import {
    AdminPageLayout,
    AdminPageHeader,
    AdminPageCard,
    AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
    SupplierAddButton,
    SupplierFilterToolbar,
    SupplierFilterField,
} from "../../common/supplierPageUi";
import {
    AdminTable,
    AdminTableHead,
    AdminTableTh,
    AdminTableBody,
    AdminTableRow,
    AdminTableCell,
    AdminTableFooter,
    TableSearchInput,
    TableEmptyRow,
    EditActionButton,
    DeleteActionButton,
    TableActionsCell,
    StatusBadge,
    getStatusVariant,
} from "../../common/adminTable";
import { paginateClientList } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import { RENTAL_FORM_TYPE_OPTIONS } from "../../../config/rentalCategoryFormConfig";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
];

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "32px",
        height: "32px",
        backgroundColor: "#ffffff",
        borderColor: "#d1d5db",
        borderRadius: "0.5rem",
        boxShadow: "none",
        "&:hover": {
            borderColor: "#d1d5db",
            backgroundColor: "#ffffff",
        },
        "&:focus": {
            borderColor: "primary",
            boxShadow: "none",
            backgroundColor: "#ffffff",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 8px",
        fontSize: "13px",
        height: "30px",
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: "0",
        fontSize: "13px",
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: "14px",
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: "14px",
        fontWeight: "500",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: "30px",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: "0 8px",
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: "0 8px",
    }),
    menu: (base) => ({
        ...base,
        fontSize: "13px",
    }),
};

const RentalCategory = () => {
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [categoryDescription, setCategoryDescription] = useState("");
    const [categoryFormType, setCategoryFormType] = useState("");
    const [categoryStatus, setCategoryStatus] = useState(1);
    const [categoryImage, setCategoryImage] = useState(null);
    const [categoryError, setCategoryError] = useState("");
    const [formTypeError, setFormTypeError] = useState("");
    const [imageError, setImageError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    const queryClient = useQueryClient();

    const {
        data: categoriesData,
        isLoading: categoriesLoading,
        error: categoriesError,
    } = useQuery({
        queryKey: ["rentalCategories"],
        queryFn: getRentalCategories,
    });

    const categories = useMemo(() => categoriesData?.data || [], [categoriesData]);

    const filteredCategories = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return categories;
        return categories.filter((category) => {
            const haystack = [
                category.name,
                category.description,
                category.form_type,
            ]
                .map((value) => String(value || "").toLowerCase())
                .join(" ");
            return haystack.includes(q);
        });
    }, [categories, searchQuery]);

    const { data: pagedCategories, pagination } = useMemo(
        () => paginateClientList(filteredCategories, currentPage),
        [filteredCategories, currentPage]
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [categories, searchQuery]);

    const resetForm = () => {
        setNewCategoryName("");
        setCategoryDescription("");
        setCategoryFormType("");
        setCategoryStatus(1);
        setCategoryImage(null);
        setCategoryError("");
        setFormTypeError("");
        setImageError("");
        setSelectedCategory(null);
    };

    const createCategoryMutation = useMutation({
        mutationFn: createRentalCategory,
        onSuccess: () => {
            setCategoryModalOpen(false);
            resetForm();
            setSuccessMessage("Category created successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalCategories"] });
        },
        onError: (error) => {
            setErrorMessage(
                error.response?.data?.message || "Failed to create category"
            );
        },
    });

    const updateCategoryMutation = useMutation({
        mutationFn: ({ id, data }) => updateRentalCategory(id, data),
        onSuccess: () => {
            setEditCategoryModalOpen(false);
            resetForm();
            setSuccessMessage("Category updated successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalCategories"] });
        },
        onError: (error) => {
            setErrorMessage(
                error.response?.data?.message || "Failed to update category"
            );
        },
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: deleteRentalCategory,
        onSuccess: () => {
            setDeleteDialogOpen(false);
            setSelectedCategory(null);
            setSuccessMessage("Category deleted successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalCategories"] });
            setIsLoading(false);
        },
        onError: (error) => {
            setErrorMessage(
                error.response?.data?.message || "Failed to delete category"
            );
            setIsLoading(false);
        },
    });

    const buildFormData = (includeImageRequired = false) => {
        setCategoryError("");
        setFormTypeError("");
        setImageError("");

        if (!newCategoryName.trim()) {
            setCategoryError("Category name is required");
            return null;
        }
        if (!categoryFormType) {
            setFormTypeError("Please select a category type.");
            return null;
        }
        if (includeImageRequired && !categoryImage) {
            setImageError("Category image is required");
            return null;
        }

        const formData = new FormData();
        formData.append("name", newCategoryName.trim());
        formData.append("description", categoryDescription || "");
        formData.append("form_type", categoryFormType);
        formData.append("is_active", categoryStatus === 1 ? "1" : "0");
        if (categoryImage) {
            formData.append("image", categoryImage);
        }
        return formData;
    };

    const handleAddCategory = () => {
        resetForm();
        setCategoryModalOpen(true);
    };

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setNewCategoryName(category.name);
        setCategoryDescription(category.description || "");
        setCategoryFormType(category.form_type || "");
        setCategoryStatus(category.is_active ? 1 : 0);
        setCategoryImage(null);
        setCategoryError("");
        setFormTypeError("");
        setImageError("");
        setEditCategoryModalOpen(true);
    };

    const handleDeleteCategory = (category) => {
        setSelectedCategory(category);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        setIsLoading(true);
        deleteCategoryMutation.mutate(selectedCategory.id);
    };

    const handleSaveCategory = () => {
        setCategoryError("");
        setImageError("");
        const formData = buildFormData(true);
        if (!formData) return;
        createCategoryMutation.mutate(formData);
    };

    const handleSaveEditCategory = () => {
        setCategoryError("");
        setImageError("");
        const formData = buildFormData(false);
        if (!formData) return;
        updateCategoryMutation.mutate({
            id: selectedCategory.id,
            data: formData,
        });
    };

    const formTypeLabel = (value) =>
        RENTAL_FORM_TYPE_OPTIONS.find((o) => o.value === value)?.label || "—";

    const formTypeSelectValue = categoryFormType
        ? RENTAL_FORM_TYPE_OPTIONS.find((o) => o.value === categoryFormType)
        : null;

    return (
        <>
            <AdminPageLayout>
                <AdminPageHeader
                    title={SUPPLIER_PAGE_META.rentalCategory.title}
                    subtitle={SUPPLIER_PAGE_META.rentalCategory.subtitle}
                    actions={
                        <SupplierAddButton onClick={handleAddCategory}>
                            Add New Category
                        </SupplierAddButton>
                    }
                />

                <AdminPageCard>
                    <AdminPageCardToolbar>
                        <SupplierFilterToolbar
                            showReset={!!searchQuery}
                            onReset={() => setSearchQuery("")}
                        >
                            <SupplierFilterField label="Search">
                                <TableSearchInput
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by category name..."
                                    className="mt-1 w-full sm:w-full"
                                />
                            </SupplierFilterField>
                        </SupplierFilterToolbar>
                    </AdminPageCardToolbar>

                    <AdminTable
                        className="shadow-none rounded-none"
                        footer={
                            !categoriesLoading &&
                            !categoriesError &&
                            filteredCategories.length > 0 ? (
                                <AdminTableFooter>
                                    <Pagination
                                        pagination={pagination}
                                        onPageChange={setCurrentPage}
                                    />
                                </AdminTableFooter>
                            ) : null
                        }
                    >
                        <AdminTableHead>
                            <AdminTableTh>#</AdminTableTh>
                            <AdminTableTh>Category Name</AdminTableTh>
                            <AdminTableTh>Image</AdminTableTh>
                            <AdminTableTh>Description</AdminTableTh>
                            <AdminTableTh>Form Type</AdminTableTh>
                            <AdminTableTh>Status</AdminTableTh>
                            <AdminTableTh>Created On</AdminTableTh>
                            <AdminTableTh align="right">Actions</AdminTableTh>
                        </AdminTableHead>
                        <AdminTableBody>
                            {categoriesLoading ? (
                                <TableEmptyRow
                                    colSpan={8}
                                    variant="simple"
                                    emptyMessage="Loading categories..."
                                />
                            ) : categoriesError ? (
                                <TableEmptyRow
                                    colSpan={8}
                                    variant="simple"
                                    emptyMessage={`Error loading categories: ${categoriesError.message}`}
                                />
                            ) : pagedCategories.length === 0 ? (
                                <TableEmptyRow
                                    colSpan={8}
                                    {...getTableEmptyProps("rentalCategory", {
                                        onAdd: handleAddCategory,
                                        hasActiveFilters: !!searchQuery,
                                        onClearFilters: () => setSearchQuery(""),
                                    })}
                                />
                            ) : (
                                pagedCategories.map((category, index) => (
                                    <AdminTableRow key={category.id}>
                                        <AdminTableCell>
                                            {(currentPage - 1) * 10 + index + 1}
                                        </AdminTableCell>
                                        <AdminTableCell>{category.name}</AdminTableCell>
                                        <AdminTableCell>
                                            {category.image ? (
                                                <img
                                                    src={category.image}
                                                    alt={category.name}
                                                    className="w-6 h-6 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                                    <i className="fi fi-rr-image text-gray-400 text-xs"></i>
                                                </div>
                                            )}
                                        </AdminTableCell>
                                        <AdminTableCell
                                            nowrap={false}
                                            className="max-w-xs truncate"
                                        >
                                            {category.description || "—"}
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            {formTypeLabel(category.form_type)}
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            <StatusBadge
                                                variant={getStatusVariant(category.is_active)}
                                            >
                                                {category.is_active ? "Active" : "Inactive"}
                                            </StatusBadge>
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            {formatListingDateTime(category.created_at)}
                                        </AdminTableCell>
                                        <TableActionsCell>
                                            <EditActionButton
                                                showLabel={false}
                                                onClick={() => handleEditCategory(category)}
                                            />
                                            <DeleteActionButton
                                                showLabel={false}
                                                onClick={() => handleDeleteCategory(category)}
                                            />
                                        </TableActionsCell>
                                    </AdminTableRow>
                                ))
                            )}
                        </AdminTableBody>
                    </AdminTable>
                </AdminPageCard>
            </AdminPageLayout>

            <Dialog
                open={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                title="Add New Category"
                size="sm"
                showCloseButton={false}
            >
                <div className="space-y-4 p-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name
                        </label>
                        <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => {
                                setNewCategoryName(e.target.value);
                                setCategoryError("");
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary ${
                                categoryError ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder="Enter category name"
                            onKeyPress={(e) =>
                                e.key === "Enter" && handleSaveCategory()
                            }
                            autoFocus
                        />
                        {categoryError && (
                            <p className="text-red-500 text-xs mt-1">{categoryError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formTypeSelectValue}
                            onChange={(o) => {
                                setCategoryFormType(o?.value || "");
                                setFormTypeError("");
                            }}
                            options={RENTAL_FORM_TYPE_OPTIONS}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select category"
                            styles={selectStyles}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Use Camping / trekking / outdoor gear for tents, backpacks,
                            sleeping bags, and trekking poles.
                        </p>
                        {formTypeError && (
                            <p className="text-red-500 text-xs mt-1">{formTypeError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={categoryDescription}
                            onChange={(e) => setCategoryDescription(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary border-gray-300 resize-none"
                            rows="3"
                            placeholder="Briefly describe this category"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={statusOptions.find(
                                (option) => option.value === categoryStatus
                            )}
                            onChange={(selectedOption) =>
                                setCategoryStatus(selectedOption?.value)
                            }
                            options={statusOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select status"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <ImageInput
                            onChange={(image) => {
                                setCategoryImage(image);
                                setImageError("");
                            }}
                            error={imageError || null}
                            label="Category Image"
                            required={true}
                            className="w-full"
                            multiple={false}
                            showPreview={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Recommended size: 300x300px
                        </p>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setCategoryModalOpen(false)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            Add Category
                        </button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={editCategoryModalOpen}
                onClose={() => setEditCategoryModalOpen(false)}
                title="Edit Category"
                size="sm"
                showCloseButton={false}
            >
                <div className="space-y-4 p-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name
                        </label>
                        <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => {
                                setNewCategoryName(e.target.value);
                                setCategoryError("");
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary ${
                                categoryError ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder="Enter category name"
                            onKeyPress={(e) =>
                                e.key === "Enter" && handleSaveEditCategory()
                            }
                            autoFocus
                        />
                        {categoryError && (
                            <p className="text-red-500 text-xs mt-1">{categoryError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formTypeSelectValue}
                            onChange={(o) => {
                                setCategoryFormType(o?.value || "");
                                setFormTypeError("");
                            }}
                            options={RENTAL_FORM_TYPE_OPTIONS}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select category"
                            styles={selectStyles}
                        />
                        {formTypeError && (
                            <p className="text-red-500 text-xs mt-1">{formTypeError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={categoryDescription}
                            onChange={(e) => setCategoryDescription(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary border-gray-300 resize-none"
                            rows="3"
                            placeholder="Briefly describe this category"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={statusOptions.find(
                                (option) => option.value === categoryStatus
                            )}
                            onChange={(selectedOption) =>
                                setCategoryStatus(selectedOption?.value)
                            }
                            options={statusOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select status"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <ImageInput
                            onChange={(image) => {
                                setCategoryImage(image);
                                setImageError("");
                            }}
                            error={imageError || null}
                            label="Category Image"
                            className="w-full"
                            multiple={false}
                            showPreview={true}
                            existingImage={selectedCategory?.image || null}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Leave empty to keep current image. Recommended size: 300x300px
                        </p>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setEditCategoryModalOpen(false)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveEditCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            Update Category
                        </button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title={
                    <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                        Delete Category
                    </div>
                }
                primaryButtonText="Delete"
                primaryButtonAction={handleConfirmDelete}
                closeButtonText="Cancel"
                closeButtonAction={() => setDeleteDialogOpen(false)}
                isLoading={isLoading}
            >
                <div className="mt-2">
                    <p className="text-sm text-gray-500">
                        Are you sure you want to delete this category? This action cannot be
                        undone.
                    </p>
                </div>
            </Dialog>

            {successMessage && (
                <Toster
                    message={successMessage}
                    type="success"
                    onClose={() => setSuccessMessage("")}
                />
            )}
            {errorMessage && (
                <Toster
                    message={errorMessage}
                    type="error"
                    onClose={() => setErrorMessage("")}
                />
            )}
        </>
    );
};

export default RentalCategory;
